<template>
  <div class="join-us-page ahrs-home-bg">
    <!-- Editorial Hero -->
    <section class="ahrs-page-hero hero-photo-registration">
      <div class="ahrs-page-hero-wrap">
        <div class="ahrs-page-eyebrow" data-aos="fade-up">
          <span class="dot"></span>Registration
        </div>
        <h1 class="ahrs-page-title" data-aos="fade-up" data-aos-delay="100">
          Reserve your seat at
          <span class="ahrs-page-italic">AHRS 2026.</span>
        </h1>
        <p class="ahrs-page-lede" data-aos="fade-up" data-aos-delay="200">
          Choose your delegate category and secure your place across the
          four-day Africa Human Resource Summit programme.
        </p>
        <div class="ahrs-page-meta" data-aos="fade-up" data-aos-delay="300">
          <div class="m">
            <svg viewBox="0 0 24 24" stroke-linecap="round">
              <rect x="3" y="4.5" width="18" height="17" rx="2" />
              <path d="M3 9h18M8 2.5v4M16 2.5v4" />
            </svg>
            19&ndash;22 August 2026
          </div>
          <div class="m">
            <svg viewBox="0 0 24 24" stroke-linecap="round">
              <path d="M12 21s7-5.6 7-11a7 7 0 1 0-14 0c0 5.4 7 11 7 11Z" />
              <circle cx="12" cy="10" r="2.6" />
            </svg>
            Mount Meru Hotel, Arusha
          </div>
        </div>
      </div>
    </section>

    <div class="pricing-container">
      <div class="selection-panel" data-aos="fade-right">
        <h2 class="section-title">Configure Your Registration</h2>

        <!-- Currency Selection -->
        <div class="dropdown-group">
          <label class="dropdown-label">Currency</label>
          <div class="custom-dropdown">
            <select
              v-model="selectedCurrency"
              class="dropdown-select"
              @change="handleCurrencyChange"
              @focus="currencyFocused = true"
              @blur="currencyFocused = false"
            >
              <option value="" :disabled="currencyFocused">
                {{ currencyFocused ? "" : "Select Currency" }}
              </option>
              <option value="USD">USD - US Dollar</option>
              <option value="TZS">TZS - Tanzanian Shilling</option>
            </select>
          </div>
        </div>

        <!-- Category Selection -->
        <div class="dropdown-group">
          <label class="dropdown-label">Membership Category</label>
          <div class="custom-dropdown">
            <select
              v-model="selectedCategory"
              class="dropdown-select"
              @focus="categoryFocused = true"
              @blur="categoryFocused = false"
            >
              <option value="" :disabled="categoryFocused">
                {{ categoryFocused ? "" : "Select Category" }}
              </option>
              <option
                v-if="!selectedCurrency || selectedCurrency === 'TZS'"
                value="THRAPA"
              >
                Tanzania Delegates (Local)
              </option>
              <option
                v-if="!selectedCurrency || selectedCurrency === 'USD'"
                value="EAHRF"
              >
                East Africa (Regional)
              </option>
              <option
                v-if="!selectedCurrency || selectedCurrency === 'USD'"
                value="AHRC"
              >
                Africa (Continental)
              </option>
              <option
                v-if="!selectedCurrency || selectedCurrency === 'USD'"
                value="International"
              >
                International (Global)
              </option>
            </select>
          </div>
        </div>

        <!-- Event Details -->
        <div class="event-details-card" data-aos="fade-up" data-aos-delay="200">
          <h3>Event Details</h3>
          <div class="detail-item">
            <strong>Date:</strong> 19-22 August 2026
          </div>
          <div class="detail-item">
            <strong>Location:</strong> Arusha, Tanzania
          </div>
          <div class="detail-item">
            <strong>Inquiries:</strong>
          </div>
          <a
            href="tel:+255745004005"
            class="detail-item contact-item contact-link"
          >
            <div class="contact-icon">
              <svg
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                stroke-width="2"
              >
                <path
                  d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z"
                ></path>
              </svg>
            </div>
            <span>+255745004005</span>
          </a>
          <a
            href="mailto:register@africahrsummit.org"
            class="detail-item contact-item contact-link"
          >
            <div class="contact-icon">
              <svg
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                stroke-width="2"
              >
                <path
                  d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"
                ></path>
                <polyline points="22,6 12,13 2,6"></polyline>
              </svg>
            </div>
            <span>register@africahrsummit.org</span>
          </a>
        </div>
      </div>

      <!-- Pricing Display -->
      <div class="pricing-panel" data-aos="fade-left">
        <div v-if="!showPricing" class="no-selection">
          <div class="selection-prompt">
            <div class="prompt-icon">$</div>
            <h3>Select Your Options</h3>
            <p>
              Please select both currency and membership category to view
              pricing details.
            </p>
          </div>
        </div>

        <div v-else class="pricing-content">
          <div class="pricing-header">
            <h2 class="pricing-title">{{ currentPricing.title }}</h2>
            <p class="pricing-subtitle">{{ currentPricing.description }}</p>
          </div>

          <div class="pricing-cards-compact">
            <!-- Participation Card -->
            <div
              class="pricing-card-compact participation-card"
              data-aos="zoom-in"
              data-aos-delay="100"
            >
              <div class="card-header">
                <div class="card-title-section">
                  <div class="checkbox-wrapper">
                    <input
                      type="radio"
                      id="participation"
                      v-model="selectedPackages.participation"
                      :value="true"
                      disabled
                      checked
                      class="package-checkbox"
                    />
                    <label for="participation" class="checkbox-label">
                      <h3 class="card-title">Participation</h3>
                      <span class="required-badge">Required</span>
                    </label>
                  </div>
                </div>
                <div class="card-price">
                  {{ formatPrice(currentPricing.participation.price) }}
                </div>
              </div>
              <div class="card-content">
                <p class="card-description">
                  {{ currentPricing.participation.description }}
                </p>
                <ul class="features-list">
                  <li>Full 4-day conference access</li>
                  <li>Access to all 50+ international speakers</li>
                  <li>All plenary sessions and breakout workshops</li>
                  <li>Premium networking events and coffee breaks</li>
                  <li>Conference materials and digital resources</li>
                  <li>Welcome reception and networking dinners</li>
                  <li>All meals and refreshments during the summit</li>
                  <li>Official THRAPA certificate with 20 CPD points</li>
                </ul>
              </div>
            </div>

            <!-- Masterclass Card -->
            <div
              class="pricing-card-compact masterclass-card"
              :class="{ selected: selectedPackages.masterclass }"
              data-aos="zoom-in"
              data-aos-delay="300"
            >
              <div class="card-header">
                <div class="card-title-section">
                  <div class="checkbox-wrapper">
                    <input
                      type="checkbox"
                      id="masterclass"
                      v-model="selectedPackages.masterclass"
                      class="package-checkbox"
                    />
                    <label for="masterclass" class="checkbox-label">
                      <h3 class="card-title">Masterclass</h3>
                      <span class="optional-badge">Optional</span>
                    </label>
                  </div>
                </div>
                <div class="card-price">
                  {{ formatPrice(currentPricing.masterclass.price) }}
                </div>
              </div>
              <div class="card-content">
                <p class="card-description">
                  {{ currentPricing.masterclass.description }}
                </p>
                <ul class="features-list">
                  <li>Duration: 21 August 2026 (Masterclass day)</li>
                  <li>4 intensive masterclass sessions (2 hours each)</li>
                  <li>Advanced HR Analytics & Data Science</li>
                  <li>Executive Leadership in Digital Transformation</li>
                  <li>Strategic Workforce Planning for Africa</li>
                  <li>Cross-Cultural HR Management</li>
                  <li>Separate masterclass completion certificate</li>
                </ul>
              </div>
            </div>
          </div>

          <!-- Tour Package Card -->
          <div
            class="pricing-card-compact tour-card"
            :class="{ selected: selectedPackages.tour }"
            data-aos="zoom-in"
            data-aos-delay="200"
          >
            <div class="card-header">
              <div class="card-title-section">
                <div class="checkbox-wrapper">
                  <input
                    type="checkbox"
                    id="tour"
                    v-model="selectedPackages.tour"
                    class="package-checkbox"
                  />
                  <label for="tour" class="checkbox-label">
                    <h3 class="card-title">Heritage Tour</h3>
                    <span class="optional-badge">Optional</span>
                  </label>
                </div>
              </div>
              <div class="card-price">
                {{ formatPrice(currentPricing.tourPackage.price) }}
              </div>
            </div>
            <div class="card-content">
              <p class="card-description">
                {{ currentPricing.tourPackage.description }}
              </p>
              <ul class="features-list">
                <li>Duration: 1 full day (22 August 2026)</li>
                <li>Transportation and guide services included</li>
                <li>All entrance fees covered</li>
                <li>Tarangire National Park game drive</li>
                <li>Elephant herds &amp; baobab landscape</li>
                <li>Traditional lunch included</li>
              </ul>
            </div>
          </div>

          <!-- Total Package -->
          <div class="total-package" data-aos="fade-up" data-aos-delay="400">
            <div class="total-card">
              <h3 class="total-title">Selected Package Total</h3>
              <div class="package-summary">
                <div class="selected-items">
                  <div class="selected-item">
                    <span>✓ Participation</span>
                    <span>{{
                      formatPrice(currentPricing.participation.price)
                    }}</span>
                  </div>
                  <div v-if="selectedPackages.tour" class="selected-item">
                    <span>✓ Tour Package</span>
                    <span>{{
                      formatPrice(currentPricing.tourPackage.price)
                    }}</span>
                  </div>
                  <div
                    v-if="selectedPackages.masterclass"
                    class="selected-item"
                  >
                    <span>✓ Masterclass</span>
                    <span>{{
                      formatPrice(currentPricing.masterclass.price)
                    }}</span>
                  </div>
                </div>
              </div>
              <div class="total-price">
                {{ formatPrice(calculateTotal()) }}
              </div>
              <p class="total-description">
                Total for {{ getSelectedPackagesCount() }} package(s)
              </p>
              <button
                class="checkout-btn"
                @click="proceedToCheckout"
                :disabled="!showPricing"
              >
                Proceed to Checkout
              </button>
            </div>
          </div>

          <!-- Payment Information -->
          <div class="payment-info" data-aos="fade-up" data-aos-delay="500">
            <h3>Payment Information</h3>
            <div class="payment-methods">
              <div class="payment-section">
                <h4>Payment Methods:</h4>
                <ul class="payment-list">
                  <li>Mobile Money</li>
                  <li>Bank Transfer</li>
                  <li>PayPal/VISA</li>
                  <!-- <li v-if="selectedCurrency === 'USD'">
                    Credit Cards (Visa, MasterCard)
                  </li> -->
                </ul>
              </div>
            </div>

            <!-- Bank Transfer Details -->
            <div class="bank-details" data-aos="fade-up" data-aos-delay="100">
              <h4>Bank Transfer Details</h4>
              <div class="bank-card">
                <div class="bank-header">
                  <span class="bank-name">CRDB Bank</span>
                  <span class="bank-branch">Branch: Temeke Taifa</span>
                </div>
                <div class="bank-account-name">
                  Tanzania Human Resource and Administration Professionals
                  Association
                </div>
                <div class="bank-accounts">
                  <div class="bank-row">
                    <span class="bank-label">Account (USD):</span>
                    <span class="bank-value">10152775817</span>
                  </div>
                  <div class="bank-row">
                    <span class="bank-label">Account (TZS):</span>
                    <span class="bank-value">015C955976800</span>
                  </div>
                  <div class="bank-row">
                    <span class="bank-label">Branch Code</span>
                    <span class="bank-value">048161</span>
                  </div>
                  <div class="bank-row">
                    <span class="bank-label">Swift Code</span>
                    <span class="bank-value">CORUTZTZ</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted } from "vue";
import { useRouter } from "vue-router";

const router = useRouter();

const selectedCurrency = ref("");
const selectedCategory = ref("");
const currencyFocused = ref(false);
const categoryFocused = ref(false);

// Package selection state
const selectedPackages = ref({
  participation: true, // Always true since it's required
  tour: false,
  masterclass: false,
});

// Restore previous selections from sessionStorage when navigating back
onMounted(() => {
  const stored = sessionStorage.getItem("checkoutData");
  if (stored) {
    const data = JSON.parse(stored);
    if (data.currency) selectedCurrency.value = data.currency;
    if (data.category) selectedCategory.value = data.category;
    if (data.packages) {
      selectedPackages.value = {
        participation: true,
        tour: data.packages.tour || false,
        masterclass: data.packages.masterclass || false,
      };
    }
  }
});

// Pricing data structure — sourced from official AHRS 2026 Concept Note
const pricingData = {
  THRAPA: {
    title: "Tanzania Delegates (Local)",
    description: "Special rates for local",
    currency: "TZS",
    participation: {
      price: 1000000,
      description:
        "Full 4-day summit access (19–22 Aug), all sessions, networking events, materials, meals & refreshments",
    },
    tourPackage: {
      price: 200000,
      description:
        "Heritage Discovery Tour — Tarangire National Park (22 Aug 2026)",
    },
    masterclass: {
      price: 250000,
      description:
        "Masterclass sessions on 21 August 2026 — AI, data protection, ethics & digital leadership",
    },
    totalPackage: 1000000,
  },
  AHRC: {
    title: "Africa Delegates",
    description: "Participation fee for African delegates",
    currency: "USD",
    participation: {
      price: 700,
      description:
        "Full 4-day summit access (19–22 Aug), all sessions, networking events, materials, meals & refreshments",
    },
    tourPackage: {
      price: 200,
      description:
        "Heritage Discovery Tour — Tarangire National Park (22 Aug 2026)",
    },
    masterclass: {
      price: 100,
      description:
        "Masterclass sessions on 21 August 2026 — AI, data protection, ethics & digital leadership",
    },
    totalPackage: 1000,
  },
  International: {
    title: "International Delegates",
    description: "Participation fee for international delegates",
    currency: "USD",
    participation: {
      price: 900,
      description:
        "Full 4-day summit access (19–22 Aug), all sessions, networking events, materials, meals & refreshments",
    },
    tourPackage: {
      price: 300,
      description:
        "Heritage Discovery Tour — Tarangire National Park (22 Aug 2026)",
    },
    masterclass: {
      price: 100,
      description:
        "Masterclass sessions on 21 August 2026 — AI, data protection, ethics & digital leadership",
    },
    totalPackage: 1300,
  },
  EAHRF: {
    title: "East Africa Delegates",
    description: "Participation fee for East African delegates",
    currency: "USD",
    participation: {
      price: 500,
      description:
        "Full 4-day summit access (19–22 Aug), all sessions, networking events, materials, meals & refreshments",
    },
    tourPackage: {
      price: 200,
      description:
        "Heritage Discovery Tour — Tarangire National Park (22 Aug 2026)",
    },
    masterclass: {
      price: 100,
      description:
        "Masterclass sessions on 21 August 2026 — AI, data protection, ethics & digital leadership",
    },
    totalPackage: 1200,
  },
};

// Computed properties
const showPricing = computed(() => {
  return (
    selectedCurrency.value &&
    selectedCategory.value &&
    ((selectedCategory.value === "THRAPA" &&
      selectedCurrency.value === "TZS") ||
      ((selectedCategory.value === "AHRC" ||
        selectedCategory.value === "International" ||
        selectedCategory.value === "EAHRF") &&
        selectedCurrency.value === "USD"))
  );
});

const currentPricing = computed(() => {
  if (!showPricing.value) return null;
  return pricingData[selectedCategory.value];
});

// Methods
const formatPrice = (price) => {
  if (!currentPricing.value) return "";

  if (currentPricing.value.currency === "TZS") {
    return `TZS ${price.toLocaleString()}`;
  } else {
    return `USD ${price.toLocaleString()}`;
  }
};

const handleCurrencyChange = () => {
  // Clear category if it's incompatible with the new currency
  if (
    (selectedCurrency.value === "USD" && selectedCategory.value === "THRAPA") ||
    (selectedCurrency.value === "TZS" &&
      (selectedCategory.value === "AHRC" ||
        selectedCategory.value === "International" ||
        selectedCategory.value === "EAHRF"))
  ) {
    selectedCategory.value = "";
  }
};

const handleRegister = () => {
  // Registration logic would be implemented here
  alert(
    `Registration initiated for ${
      currentPricing.value.title
    }. Total: ${formatPrice(currentPricing.value.totalPackage)}`,
  );
};

const calculateTotal = () => {
  if (!currentPricing.value) return 0;

  let total = currentPricing.value.participation.price; // Participation is always included

  if (selectedPackages.value.tour) {
    total += currentPricing.value.tourPackage.price;
  }

  if (selectedPackages.value.masterclass) {
    total += currentPricing.value.masterclass.price;
  }

  return total;
};

const getSelectedPackagesCount = () => {
  let count = 1; // Participation is always selected
  if (selectedPackages.value.tour) count++;
  if (selectedPackages.value.masterclass) count++;
  return count;
};

const proceedToCheckout = () => {
  // Merge with existing sessionStorage data so billing details filled on
  // a previous visit to checkout are not lost when navigating back here
  const existing = JSON.parse(sessionStorage.getItem("checkoutData") || "{}");

  const checkoutData = {
    ...existing,
    currency: selectedCurrency.value,
    category: selectedCategory.value,
    packages: selectedPackages.value,
    pricing: currentPricing.value,
    total: calculateTotal(),
  };

  sessionStorage.setItem("checkoutData", JSON.stringify(checkoutData));
  router.push("/checkout");
};
</script>

<style scoped>
/* Editorial hero backdrop photo */
.hero-photo-registration {
  --hero-photo: url("~@/assets/images/past Ahrs/Photo.jpg");
}

.join-us-page {
  min-height: 100vh;
  color: #fbf7ec;
  padding-top: 80px;
}

.page-header {
  position: relative;
  text-align: center;
  padding: 60px 20px 40px;
  overflow: hidden;
}

.header-background {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: radial-gradient(
    ellipse at center,
    rgba(74, 222, 128, 0.1) 0%,
    transparent 70%
  );
  animation: pulse 4s ease-in-out infinite;
}

@keyframes pulse {
  0%,
  100% {
    opacity: 0.1;
    transform: scale(1);
  }
  50% {
    opacity: 0.3;
    transform: scale(1.05);
  }
}

.header-content {
  position: relative;
  z-index: 2;
  max-width: 800px;
  margin: 0 auto;
}

.page-title {
  font-size: 4rem;
  font-weight: 900;
  margin-bottom: 20px;
  background: linear-gradient(
    45deg,
    var(--color-primary-light),
    #ffffff,
    var(--color-primary-light)
  );
  background-size: 200% 200%;
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  animation: textShimmer 3s ease-in-out infinite;
  text-shadow: 0 0 30px rgba(74, 222, 128, 0.5);
}

.page-subtitle {
  font-size: 1.5rem;
  color: rgba(251, 247, 236, 0.92);
  margin-bottom: 30px;
  letter-spacing: 1px;
}

.event-highlight {
  background: rgba(74, 222, 128, 0.1);
  backdrop-filter: blur(10px);
  border: 1px solid rgba(74, 222, 128, 0.3);
  border-radius: 30px;
  padding: 15px 30px;
  display: inline-block;
}

.highlight-text {
  font-size: 1.2rem;
  font-style: italic;
  color: #b8f0c8;
}

.pricing-container {
  max-width: 1400px;
  margin: 0 auto;
  padding: 30px 20px;
  display: grid;
  grid-template-columns: 1fr 2fr;
  gap: 30px;
  align-items: start;
}

.selection-panel {
  background: rgba(255, 255, 255, 0.05);
  backdrop-filter: blur(20px);
  border: 1px solid rgba(74, 222, 128, 0.2);
  border-radius: 20px;
  padding: 30px;
  position: sticky;
  top: 100px;
}

.section-title {
  font-size: 1.8rem;
  font-weight: 700;
  margin-bottom: 30px;
  color: #b8f0c8;
  text-align: center;
}

.dropdown-group {
  margin-bottom: 25px;
}

.dropdown-label {
  display: block;
  font-size: 1.1rem;
  font-weight: 600;
  margin-bottom: 8px;
  color: rgba(251, 247, 236, 0.92);
}

.custom-dropdown {
  position: relative;
}

.dropdown-select {
  width: 100%;
  padding: 15px 20px;
  background: rgba(74, 222, 128, 0.1);
  border: 2px solid rgba(74, 222, 128, 0.3);
  border-radius: 12px;
  color: #fbf7ec;
  font-size: 1rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.3s ease;
  backdrop-filter: blur(10px);
}

.dropdown-select:focus {
  outline: none;
  border-color: #b8f0c8;
  box-shadow: 0 0 20px rgba(74, 222, 128, 0.3);
}

.dropdown-select option {
  background: #ffffff;
  color: #0a3a1f;
  padding: 10px;
}

.dropdown-select option:disabled {
  background: #0f1f13;
  color: rgba(251, 247, 236, 0.5);
  cursor: not-allowed;
}

.event-details-card {
  background: rgba(74, 222, 128, 0.08);
  border: 1px solid rgba(74, 222, 128, 0.2);
  border-radius: 15px;
  padding: 25px;
  margin-top: 30px;
}

.event-details-card h3 {
  font-size: 1.3rem;
  font-weight: 700;
  margin-bottom: 20px;
  color: #b8f0c8;
}

.detail-item {
  margin-bottom: 12px;
  line-height: 1.5;
  color: rgba(251, 247, 236, 0.92);
}

.contact-item {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-left: 20px;
}

.contact-icon {
  width: 20px;
  height: 20px;
  color: #b8f0c8;
}

.contact-icon svg {
  width: 100%;
  height: 100%;
}

.contact-link {
  text-decoration: none;
  color: inherit;
  transition: all 0.3s ease;
  border-radius: 8px;
  padding: 8px 12px;
  margin: 4px 0;
}

.contact-link:hover {
  background: rgba(255, 255, 255, 0.1);
  transform: translateX(5px);
}

.contact-link:hover .contact-icon {
  color: var(--color-accent);
}

.pricing-panel {
  background: rgba(255, 255, 255, 0.03);
  border-radius: 20px;
  padding: 0;
  min-height: 600px;
}

.no-selection {
  display: flex;
  align-items: center;
  justify-content: center;
  height: 600px;
  text-align: center;
}

.selection-prompt {
  opacity: 0.6;
}

.prompt-icon {
  font-size: 4rem;
  margin-bottom: 20px;
}

.selection-prompt h3 {
  font-size: 1.8rem;
  margin-bottom: 15px;
  color: #b8f0c8;
}

.selection-prompt p {
  font-size: 1.1rem;
  color: rgba(251, 247, 236, 0.82);
}

.pricing-content {
  padding: 30px;
}

.pricing-header {
  text-align: center;
  margin-bottom: 40px;
}

.pricing-title {
  font-size: 2.2rem;
  font-weight: 800;
  margin-bottom: 10px;
  color: #b8f0c8;
}

.pricing-subtitle {
  font-size: 1.1rem;
  color: rgba(251, 247, 236, 0.82);
}

.pricing-cards-compact {
  display: grid;
  grid-template-columns: 1fr;
  gap: 15px;
  margin-bottom: 30px;
}

.pricing-card-compact {
  background: rgba(74, 222, 128, 0.08);
  border: 2px solid rgba(74, 222, 128, 0.2);
  border-radius: 12px;
  padding: 20px;
  transition: all 0.3s ease;
  position: relative;
}

.pricing-card {
  background: rgba(74, 222, 128, 0.08);
  border: 2px solid rgba(74, 222, 128, 0.2);
  border-radius: 16px;
  padding: 25px;
  transition: all 0.3s ease;
  position: relative;
}

.pricing-card-compact:hover,
.pricing-card:hover {
  transform: translateY(-3px);
  box-shadow: 0 10px 25px rgba(74, 222, 128, 0.15);
  border-color: rgba(74, 222, 128, 0.4);
}

.card-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 20px;
  position: relative;
}

.card-title-section {
  flex: 1;
}

.checkbox-wrapper {
  display: flex;
  align-items: flex-start;
  gap: 15px;
}

.package-checkbox {
  width: 20px;
  height: 20px;
  margin-top: 5px;
  accent-color: #b8f0c8;
  cursor: pointer;
}

.package-checkbox:disabled {
  cursor: not-allowed;
}

.checkbox-label {
  display: flex;
  flex-direction: column;
  cursor: pointer;
  gap: 8px;
}

.checkbox-label:has(input:disabled) {
  cursor: not-allowed;
}

.card-title {
  font-size: 1.5rem;
  font-weight: 700;
  color: #b8f0c8;
  margin: 0;
}

.required-badge {
  background: linear-gradient(45deg, #ef4444, #dc2626);
  color: #fbf7ec;
  padding: 4px 10px;
  border-radius: 10px;
  font-size: 0.7rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 1px;
}

.pricing-card-compact.selected {
  border-color: #b8f0c8;
  box-shadow: 0 0 20px rgba(74, 222, 128, 0.3);
  transform: translateY(-2px);
}

.package-summary {
  margin-bottom: 20px;
}

.selected-items {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.selected-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px 20px;
  background: rgba(74, 222, 128, 0.1);
  border-radius: 10px;
  border: 1px solid rgba(74, 222, 128, 0.2);
}

.selected-item span:first-child {
  color: #b8f0c8;
  font-weight: 600;
}

.selected-item span:last-child {
  color: #fbf7ec;
  font-weight: 700;
}

.checkout-btn {
  background: linear-gradient(
    135deg,
    var(--color-primary-light),
    var(--color-success)
  );
  color: #fbf7ec;
  border: none;
  padding: 20px 50px;
  font-size: 1.3rem;
  font-weight: 900;
  border-radius: 50px;
  cursor: pointer;
  text-transform: uppercase;
  letter-spacing: 2px;
  transition: all 0.4s ease;
  box-shadow: 0 0 30px rgba(74, 222, 128, 0.4);
}

.checkout-btn:hover:not(:disabled) {
  transform: translateY(-3px) scale(1.05);
  box-shadow: 0 0 50px rgba(74, 222, 128, 0.6);
}

.checkout-btn:disabled {
  background: rgba(255, 255, 255, 0.1);
  color: rgba(251, 247, 236, 0.65);
  cursor: not-allowed;
  box-shadow: none;
}

.card-price {
  font-size: 1.8rem;
  font-weight: 900;
  color: #fbf7ec;
  text-shadow: 0 0 10px rgba(74, 222, 128, 0.5);
}

.optional-badge {
  background: linear-gradient(
    45deg,
    var(--color-primary-light),
    var(--color-success)
  );
  color: #fbf7ec;
  padding: 4px 10px;
  border-radius: 10px;
  font-size: 0.7rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 1px;
}

.card-description {
  color: rgba(251, 247, 236, 0.92);
  margin-bottom: 20px;
  line-height: 1.5;
}

.features-list {
  list-style: none;
  padding: 0;
  margin: 0;
}

.features-list li {
  padding: 8px 0;
  color: rgba(251, 247, 236, 0.82);
  border-bottom: 1px solid rgba(74, 222, 128, 0.1);
  line-height: 1.4;
}

.features-list li:last-child {
  border-bottom: none;
}

.total-package {
  text-align: center;
  margin: 40px 0;
}

.total-card {
  background: linear-gradient(
    135deg,
    rgba(74, 222, 128, 0.15),
    rgba(34, 197, 94, 0.1)
  );
  border: 2px solid var(--color-primary-light);
  border-radius: 20px;
  padding: 40px;
  box-shadow: 0 10px 40px rgba(74, 222, 128, 0.2);
}

.total-title {
  font-size: 2rem;
  font-weight: 800;
  margin-bottom: 15px;
  color: #b8f0c8;
}

.total-price {
  font-size: 3rem;
  font-weight: 900;
  color: #fbf7ec;
  text-shadow: 0 0 20px var(--color-primary-light);
  margin-bottom: 15px;
}

.total-description {
  font-size: 1.1rem;
  color: rgba(251, 247, 236, 0.82);
  margin-bottom: 30px;
}

.register-btn {
  background: linear-gradient(
    135deg,
    var(--color-primary-light),
    var(--color-success)
  );
  color: #fbf7ec;
  border: none;
  padding: 20px 50px;
  font-size: 1.3rem;
  font-weight: 900;
  border-radius: 50px;
  cursor: pointer;
  text-transform: uppercase;
  letter-spacing: 2px;
  transition: all 0.4s ease;
  box-shadow: 0 0 30px rgba(74, 222, 128, 0.4);
}

.register-btn:hover {
  transform: translateY(-3px) scale(1.05);
  box-shadow: 0 0 50px rgba(74, 222, 128, 0.6);
}

.payment-info {
  background: rgba(74, 222, 128, 0.05);
  border: 1px solid rgba(74, 222, 128, 0.2);
  border-radius: 15px;
  padding: 30px;
  margin-top: 40px;
}

.payment-info h3 {
  font-size: 1.5rem;
  font-weight: 700;
  margin-bottom: 25px;
  color: #b8f0c8;
  text-align: center;
}

.payment-methods {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 30px;
}

.payment-section h4 {
  font-size: 1.2rem;
  font-weight: 600;
  margin-bottom: 15px;
  color: #b8f0c8;
}

.payment-list {
  list-style: none;
  padding: 0;
  margin: 0;
}

.payment-list li {
  padding: 8px 0;
  color: rgba(251, 247, 236, 0.92);
  border-bottom: 1px solid rgba(74, 222, 128, 0.1);
}

.payment-list li:last-child {
  border-bottom: none;
}

.bank-details {
  margin-top: 30px;
  border-top: 1px solid rgba(74, 222, 128, 0.2);
  padding-top: 25px;
}

.bank-details h4 {
  font-size: 1.2rem;
  font-weight: 600;
  margin-bottom: 15px;
  color: #b8f0c8;
}

.bank-card {
  background: rgba(26, 71, 42, 0.4);
  border: 1px solid rgba(74, 222, 128, 0.3);
  border-radius: 12px;
  padding: 20px 25px;
}

.bank-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 8px;
}

.bank-name {
  font-size: 1.1rem;
  font-weight: 700;
  color: #b8f0c8;
}

.bank-branch {
  font-size: 0.9rem;
  color: rgba(251, 247, 236, 0.65);
}

.bank-account-name {
  font-size: 0.95rem;
  color: rgba(251, 247, 236, 0.82);
  margin-bottom: 16px;
  padding-bottom: 12px;
  border-bottom: 1px solid rgba(74, 222, 128, 0.15);
}

.bank-accounts {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 10px;
}

.bank-row {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.bank-label {
  font-size: 0.75rem;
  color: rgba(251, 247, 236, 0.65);
  text-transform: uppercase;
  letter-spacing: 0.05em;
}

.bank-value {
  font-size: 0.95rem;
  font-weight: 600;
  color: #fbf7ec;
  font-family: monospace;
  letter-spacing: 0.03em;
}

@keyframes textShimmer {
  0%,
  100% {
    background-position: 0% 50%;
  }
  50% {
    background-position: 100% 50%;
  }
}

/* Responsive Design */
@media (max-width: 1200px) {
  .pricing-container {
    grid-template-columns: 1fr;
    gap: 24px;
  }

  .selection-panel {
    position: relative;
    top: 0;
  }
}

@media (max-width: 768px) {
  .page-title {
    font-size: 2.5rem;
  }

  .pricing-container {
    padding: 20px 14px;
    gap: 20px;
  }

  .selection-panel,
  .pricing-content {
    padding: 22px 18px;
    border-radius: 16px;
  }

  .section-title {
    font-size: 1.4rem;
    margin-bottom: 22px;
  }

  .dropdown-group {
    margin-bottom: 18px;
  }

  .dropdown-label {
    font-size: 0.95rem;
  }

  .dropdown-select {
    padding: 13px 16px;
    font-size: 0.95rem;
  }

  /* Pricing card grid: stack the inner participation+masterclass pair */
  .pricing-cards-compact {
    grid-template-columns: 1fr !important;
    gap: 16px;
  }

  /* Make each pricing card stack title/price vertically so neither truncates */
  .pricing-card-compact {
    padding: 18px;
    border-radius: 14px;
  }

  .card-header {
    flex-direction: column;
    align-items: flex-start;
    gap: 8px;
    margin-bottom: 12px;
  }

  .card-title-section {
    width: 100%;
  }

  .checkbox-wrapper {
    gap: 10px;
  }

  .checkbox-label {
    flex-wrap: wrap;
    gap: 8px;
  }

  .card-title {
    font-size: 1.15rem;
  }

  .required-badge,
  .optional-badge {
    font-size: 0.7rem;
    padding: 3px 9px;
  }

  .card-price {
    font-size: 1.5rem;
    align-self: flex-start;
  }

  .card-description {
    font-size: 0.9rem;
    line-height: 1.5;
    margin-bottom: 10px;
  }

  .features-list {
    font-size: 0.85rem;
    padding-left: 18px;
  }

  .features-list li {
    margin-bottom: 6px;
    line-height: 1.45;
  }

  /* Total summary */
  .total-card {
    padding: 22px 18px;
  }

  .total-title {
    font-size: 1.1rem;
  }

  .selected-item {
    font-size: 0.9rem;
    padding: 7px 0;
  }

  .total-price {
    font-size: 2rem;
  }

  .total-description {
    font-size: 0.85rem;
  }

  .checkout-btn {
    width: 100%;
    padding: 15px 20px;
    font-size: 1rem;
  }

  /* Payment info */
  .payment-methods {
    grid-template-columns: 1fr;
    gap: 20px;
  }

  .payment-info h3 {
    font-size: 1.15rem;
  }

  .bank-card {
    padding: 16px;
  }

  .bank-header {
    flex-direction: column;
    align-items: flex-start;
    gap: 4px;
  }

  .bank-name {
    font-size: 1rem;
  }

  .bank-row {
    flex-direction: column;
    align-items: flex-start;
    gap: 2px;
    padding: 8px 0;
  }

  .bank-value {
    font-size: 0.95rem;
    word-break: break-all;
  }

  .register-btn {
    padding: 16px 32px;
    font-size: 1rem;
  }

  /* Event details card */
  .event-details-card {
    padding: 18px;
  }

  .event-details-card h3 {
    font-size: 1.1rem;
    margin-bottom: 14px;
  }

  .detail-item {
    font-size: 0.92rem;
  }
}

@media (max-width: 480px) {
  .page-title {
    font-size: 1.8rem;
  }

  .pricing-container {
    padding: 16px 10px;
    gap: 16px;
  }

  .selection-panel,
  .pricing-content {
    padding: 18px 14px;
    border-radius: 14px;
  }

  .section-title {
    font-size: 1.25rem;
  }

  .pricing-card-compact {
    padding: 16px 14px;
  }

  .card-title {
    font-size: 1.05rem;
  }

  .card-price {
    font-size: 1.35rem;
  }

  .features-list {
    font-size: 0.82rem;
    padding-left: 16px;
  }

  .total-price {
    font-size: 1.75rem;
  }

  .total-card {
    padding: 18px 14px;
  }

  .checkout-btn {
    font-size: 0.95rem;
    padding: 14px 16px;
  }
}
</style>
